import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveOnsiteLiveTeam } from '@/api/onsiteLiveTeams';
import { AlertDialog } from '@/components/ui/AlertDialog';
import type { OnsiteLiveTeam } from '@/types/oscar';
import { ErrorBanner, FieldError } from '../BranchRelationships/shared';
import { btnPrimary, btnSecondary, inputCls } from '../BranchRelationships/styles';
import { TEAMS_KEY } from './keys';
import { DEFAULT_TEAM_ID, emptyTeam, teamSchema, teamToForm, toTeamPayload, type TeamFormData } from './schema';

type Editing = { mode: 'edit'; team: OnsiteLiveTeam } | { mode: 'new' } | null;

/**
 * The approval teams (oscar.Team = Onsitelive.DAL.Team). TeamId 1–21 are Baseplan
 * team codes; 22–26 are OnsiteLive-only (Sime Industrial, managed by hand, D14).
 * A team some department maps to cannot be switched off: DAL.vwLimits drops
 * inactive teams, so its people would lose every limit.
 */
export function TeamsTab({ teams, loading, canWrite }: { teams: OnsiteLiveTeam[]; loading: boolean; canWrite: boolean }) {
  const [editing, setEditing] = useState<Editing>(null);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1 overflow-x-auto rounded border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-navy text-left text-xs text-white">
            <tr>
              <th className="px-3 py-2">Id</th>
              <th className="px-3 py-2">Team</th>
              <th className="px-3 py-2">OLT</th>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2 text-right">Users</th>
              <th className="px-3 py-2 text-right">Departments</th>
              <th className="px-3 py-2 text-right">Limits</th>
              {canWrite && <th className="px-3 py-2"><span className="sr-only">Edit</span></th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-3 text-darkGrey/60">Loading…</td></tr>}
            {teams.map((t, i) => (
              <tr key={t.TeamId} className={i % 2 ? 'bg-[#F4F6F9]' : 'bg-white'}>
                <td className="px-3 py-1.5 font-mono text-xs text-darkGrey/70">{t.TeamId}</td>
                <td className={`px-3 py-1.5 ${t.IsActive ? 'text-darkGrey' : 'text-darkGrey/50 line-through'}`}>
                  {t.TeamCode}
                  {t.TeamId === DEFAULT_TEAM_ID && <span className="ml-2 text-xs text-darkGrey/60">(unmapped departments fall here)</span>}
                </td>
                <td className="px-3 py-1.5">{t.OLT ? 'Yes' : ''}</td>
                <td className="px-3 py-1.5">{t.IsActive ? 'Yes' : 'No'}</td>
                <td className="px-3 py-1.5 text-right">{t.ActiveUsers}</td>
                <td className="px-3 py-1.5 text-right">{t.Departments}</td>
                <td className="px-3 py-1.5 text-right">{t.Limits}</td>
                {canWrite && (
                  <td className="px-3 py-1.5 text-right">
                    <button type="button" className={btnSecondary} onClick={() => setEditing({ mode: 'edit', team: t })} aria-label={`Edit ${t.TeamCode}`}>
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="lg:w-80 lg:shrink-0">
        {canWrite && editing === null && (
          <button type="button" className={btnPrimary} onClick={() => setEditing({ mode: 'new' })}>Add team</button>
        )}
        {editing && (
          <TeamEditor
            key={editing.mode === 'edit' ? editing.team.TeamId : 'new'}
            team={editing.mode === 'edit' ? editing.team : null}
            onDone={() => setEditing(null)}
          />
        )}
        <p className="mt-3 text-xs text-darkGrey/70">
          Team 1–21 names come from Baseplan (<span className="font-mono">Users_Team</span>); keep them the same there.
          OLT = leadership team: OnFlow&apos;s single-source approvals go up the manager chain to the first OLT team.
        </p>
      </div>
    </div>
  );
}

function TeamEditor({ team, onDone }: { team: OnsiteLiveTeam | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState<TeamFormData | null>(null);
  const { register, handleSubmit, formState, reset } = useForm<TeamFormData>({
    resolver: zodResolver(teamSchema),
    defaultValues: team ? teamToForm(team) : emptyTeam,
  });
  useEffect(() => { reset(team ? teamToForm(team) : emptyTeam); }, [team, reset]);

  const save = useMutation({
    mutationFn: (d: TeamFormData) => saveOnsiteLiveTeam(team ? team.TeamId : null, toTeamPayload(d, team === null)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEAMS_KEY });
      onDone();
    },
    onError: (e) => { setConfirmOff(null); setError(e instanceof Error ? e.message : 'Could not save the team.'); },
  });

  const submit = (d: TeamFormData) => {
    setError(null);
    // Switching off a team people are on takes every limit off them (DAL.vwLimits).
    if (team && team.IsActive && !d.IsActive && team.ActiveUsers > 0) setConfirmOff(d);
    else save.mutate(d);
  };
  const err = formState.errors;

  return (
    <form
      className="rounded border border-border bg-white p-4"
      aria-label={team ? `Team ${team.TeamId}` : 'New team'}
      onSubmit={(e) => { e.preventDefault(); void handleSubmit(submit)(); }}
    >
      <h2 className="font-heading text-base font-bold text-navy">{team ? `Team ${team.TeamId}` : 'New team'}</h2>
      <fieldset disabled={save.isPending} className="mt-3 space-y-3">
        {team === null && (
          <label className="flex flex-col text-xs font-semibold text-darkGrey">
            Team id
            <input {...register('TeamId')} inputMode="numeric" className={`${inputCls} mt-1 font-normal`} />
            <span className="mt-1 font-normal text-darkGrey/70">The Baseplan team code, or an unused number for an OnsiteLive-only team. Cannot change later.</span>
            <FieldError message={err.TeamId?.message} />
          </label>
        )}
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Team name
          <input {...register('TeamCode')} className={`${inputCls} mt-1 font-normal`} />
          <FieldError message={err.TeamCode?.message} />
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-darkGrey">
          <input type="checkbox" {...register('OLT')} /> OLT (leadership team)
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-darkGrey">
          <input type="checkbox" {...register('IsActive')} /> Active
        </label>
      </fieldset>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <div className="mt-4 flex gap-2">
        <button type="submit" className={btnPrimary} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        <button type="button" className={btnSecondary} onClick={onDone} disabled={save.isPending}>Cancel</button>
      </div>

      {confirmOff && team && (
        <AlertDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmOff(null); }}
          title={`Switch off ${team.TeamCode}?`}
          description={`${team.ActiveUsers} active user(s) are on this team. An inactive team has no limits, so they lose PO, credit and OnFlow approval authority until they move team.`}
          confirmLabel="Switch off"
          destructive
          onConfirm={() => save.mutate(confirmOff)}
        />
      )}
    </form>
  );
}
